import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MERCHANT_ROUTES } from '@dukene/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPaymentLink, CreatePaymentLinkPayload } from '../../api/paymentLinksApi';
import {
  ArrowLeft,
  CurrencyDollar,
  FloppyDisk,
  Storefront,
  Spinner,
} from '@phosphor-icons/react';

export default function CreatePaymentLink() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCustom, setIsCustom] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    productId: '',
    expiresAt: '',
    referenceCode: '',
    customerName: '',
    customerPhone: '',
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: (payload: CreatePaymentLinkPayload) => createPaymentLink(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentLinks'] });
      navigate(MERCHANT_ROUTES.CHECKOUT_LINKS);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate({
      title: formData.title,
      amount: formData.amount,
      referenceCode: formData.referenceCode || undefined,
      expiresAt: formData.expiresAt || undefined,
      productId: isCustom ? undefined : formData.productId,
      customerName: formData.customerName || undefined,
      customerPhone: formData.customerPhone || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-4xl">
      {/* Polaris High-Density Top Bar */}
      <div className="flex items-center justify-between gap-3 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(MERCHANT_ROUTES.CHECKOUT_LINKS)}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              Create Shareable Payment Link
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400">
              Generate a fast checkout link to collect mobile money or card payments instantly
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(MERCHANT_ROUTES.CHECKOUT_LINKS)}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white transition-colors"
          >
            Discard
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            {isPending ? (
               <Spinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
               <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
            )}
            {isPending ? 'Generating...' : 'Generate Link'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-200">
          {(error as any)?.response?.data?.error || 'Failed to create payment link.'}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Link Type & Item Details
            </h3>

            {/* Type Switcher */}
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800">
              <button
                type="button"
                onClick={() => setIsCustom(true)}
                className={`py-2 px-3 rounded-lg text-xs font-bold transition-all inline-flex items-center justify-center gap-1.5 ${
                  isCustom
                    ? 'bg-white dark:bg-[#1A1D21] shadow-xs text-[#006838]'
                    : 'text-[#5C5F62] hover:text-[#202223]'
                }`}
              >
                <CurrencyDollar weight="bold" className="w-4 h-4" />
                Custom Amount / Fee
              </button>
              <button
                type="button"
                onClick={() => setIsCustom(false)}
                className={`py-2 px-3 rounded-lg text-xs font-bold transition-all inline-flex items-center justify-center gap-1.5 ${
                  !isCustom
                    ? 'bg-white dark:bg-[#1A1D21] shadow-xs text-[#006838]'
                    : 'text-[#5C5F62] hover:text-[#202223]'
                }`}
              >
                <Storefront weight="bold" className="w-4 h-4" />
                Existing Catalog Product
              </button>
            </div>

            {isCustom ? (
              <div className="space-y-3 pt-1">
                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Link Title / Service Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. VIP Styling Consultation Fee, Deposit #409"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      Amount (KES) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full h-9 px-3 font-mono font-bold rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                      Custom Reference ID (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. INV-2026-88"
                      value={formData.referenceCode}
                      onChange={(e) => setFormData({ ...formData, referenceCode: e.target.value })}
                      className="w-full h-9 px-3 font-mono uppercase rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Select Product from Catalog <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  >
                    <option value="">Choose product item...</option>
                  </select>
                  <p className="mt-1 text-[10px] text-[#5C5F62]">
                    Products will appear here when payment links are connected to your catalog.
                  </p>
                </div>
                {/* Fallback inputs since products aren't fetched in this simplified view yet */}
                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Link Title (Product Name) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Product Name"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                    Amount (KES) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full h-9 px-3 font-mono font-bold rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Expiry & Settings
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                Link Expiration Date
              </label>
              <input
                type="date"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
              />
              <p className="text-[10px] text-[#5C5F62] mt-1">
                Leave empty for a permanent payment link that never expires.
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62]">
              Customer Details (Optional)
            </h3>
            
            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                Customer Name
              </label>
              <input
                type="text"
                placeholder="e.g. John Doe"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-white mb-1">
                Customer Phone
              </label>
              <input
                type="text"
                placeholder="e.g. 254712345678"
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
              />
              <p className="text-[10px] text-[#5C5F62] mt-1">
                Pre-fills the Lipwa checkout form with this phone number.
              </p>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
